import { useState, useEffect, useCallback } from "react";
import { GestionHeader } from "./SiteHeader";
import CreateCardModal from "./CreateCardModal";
import EditCardModal from "./EditCardModal";
import { apiGet, errorMessage } from "./api";
import "./CardManagement.css";

function CardManagement({ user, onNavigate, onLogout }) {
  const [cards, setCards] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editingCard, setEditingCard] = useState(null);

  useEffect(() => {
    apiGet("/api/organizations").then(setOrgs).catch(() => setOrgs([]));
  }, []);

  const fetchCards = useCallback(async (signal) => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 20 });
    if (search) params.append("search", search);
    if (statusFilter) params.append("status", statusFilter);
    if (platformFilter) params.append("platform", platformFilter);
    if (orgFilter) params.append("organization", orgFilter);

    try {
      const data = await apiGet(`/api/cards?${params}`, "Impossible de charger les cartes");
      if (signal?.aborted) return;
      setCards(data.cards);
      setTotalPages(data.pages);
      setTotal(data.total);
      setError("");
    } catch (e) {
      if (signal?.aborted) return;
      setCards([]);
      setTotal(0);
      setTotalPages(1);
      setError(errorMessage(e, "Impossible de charger les cartes"));
    }
    if (!signal?.aborted) setLoading(false);
  }, [page, search, statusFilter, platformFilter, orgFilter]);

  // Debounce : une requête par frappe sinon, avec réponses dans le désordre
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => fetchCards(controller.signal), 300);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [fetchCards]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="cm-container">
      <GestionHeader
        user={user}
        onNavigate={onNavigate}
        title="Gestion Cartes"
        onBack={() => onNavigate("admin")}
        onHome={() => onNavigate("home")}
        onLogout={onLogout}
      />

      <main className="cm-main">
        <div className="cm-filters">
          <input
            type="text"
            className="cm-search"
            placeholder="Rechercher (ID, nom, prénom, email)..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          <select className="cm-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">Tous les statuts</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
            <option value="banned">Banned</option>
          </select>
          <select className="cm-select" value={platformFilter} onChange={(e) => { setPlatformFilter(e.target.value); setPage(1); }}>
            <option value="">Toutes les plateformes</option>
            <option value="Android">Android</option>
            <option value="Apple">Apple</option>
          </select>
          <select className="cm-select" value={orgFilter} onChange={(e) => { setOrgFilter(e.target.value); setPage(1); }}>
            <option value="">Toutes les organisations</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>

        <div className="cm-toolbar">
          <p className="cm-count">{total} carte{total > 1 ? "s" : ""}</p>
          <button className="cm-btn-create" onClick={() => setShowCreate(true)}>+ Créer une carte</button>
        </div>

        {error && <div className="cm-error">{error}</div>}

        {loading ? (
          <p className="cm-loading">Chargement...</p>
        ) : cards.length === 0 ? (
          <p className="cm-empty">Aucune carte trouvée</p>
        ) : (
          <>
            <div className="cm-table-wrapper">
              <table className="cm-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nom</th>
                    <th>Prénom</th>
                    <th>Email</th>
                    <th>Points</th>
                    <th>Plateforme</th>
                    <th>Statut</th>
                    <th>Création</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cards.map((c) => (
                    <tr key={c.id}>
                      <td className="cm-id">{c.id}</td>
                      <td>{c.name}</td>
                      <td>{c.surname}</td>
                      <td>{c.email}</td>
                      <td><span className="cm-points">{c.points}</span></td>
                      <td>{c.platform || "-"}</td>
                      <td><span className={`cm-badge cm-badge-${c.status}`}>{c.status}</span></td>
                      <td>{formatDate(c.created_at)}</td>
                      <td><button className="cm-btn-edit" onClick={() => setEditingCard(c)}>Modifier</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="cm-cards">
              {cards.map((c) => (
                <div className="cm-card" key={c.id}>
                  <div className="cm-card-header">
                    <strong>{c.surname} {c.name}</strong>
                    <span className={`cm-badge cm-badge-${c.status}`}>{c.status}</span>
                  </div>
                  <div className="cm-card-id">{c.id}</div>
                  <div className="cm-card-row">{c.email}</div>
                  <div className="cm-card-footer">
                    <span className="cm-points">{c.points} pts</span>
                    <span className="cm-card-platform">{c.platform || "-"}</span>
                    <span className="cm-card-date">{formatDate(c.created_at)}</span>
                  </div>
                  <button className="cm-btn-edit cm-btn-edit-mobile" onClick={() => setEditingCard(c)}>Modifier</button>
                </div>
              ))}
            </div>
          </>
        )}

        {totalPages > 1 && (
          <div className="cm-pagination">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Précédent</button>
            <span>{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Suivant</button>
          </div>
        )}

        {showCreate && (
          <CreateCardModal
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); setPage(1); fetchCards(); }}
          />
        )}
        {editingCard && (
          <EditCardModal
            cardToEdit={editingCard}
            onClose={() => setEditingCard(null)}
            onUpdated={() => { setEditingCard(null); fetchCards(); }}
          />
        )}
      </main>
    </div>
  );
}

export default CardManagement;
